export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig();
  const tokenStore = useTokenStore();
  
  const { openToast, openOkAlert } = useUserMessages();
  const { t, locale } = useEkI18n();

  function formatValidationErrors(validationErrors: string[] | undefined): string {
    if (!validationErrors || !Array.isArray(validationErrors) || validationErrors.length === 0) {
      return '';
    }

    return validationErrors.map(err => `• ${err}`).join('\n');
  }
  
  function displayValidationErrors(apiResponse: ResponseDTO<unknown>, defaultMessage: string) {
    const errorMessage = apiResponse?.ErrorMessage || defaultMessage;
    
    openToast(errorMessage, ToastType.ERROR);
    
    if (apiResponse?.ValidationErrors?.length > 0) {
      openOkAlert(
        errorMessage, 
        formatValidationErrors(apiResponse.ValidationErrors)
      );
    }
  }

  const appApiFetchV2Anonymous = $fetch.create({
    baseURL: config.public.apiBase,
    async onRequest(context) {

      context.options.headers.append(
        'Localization', locale.value
      );
    },
    onRequestError() {
      openToast(t('alert.requestFailed'), ToastType.ERROR);
    },
    onResponseError({ response }) {
      // have to call AsErrorResponse in appapi on all badrequests calls 
      const apiResponse = response._data as ResponseDTO<unknown>;
  
      switch (response?.status) {
        case 400:
          displayValidationErrors(apiResponse, t('alert.requestFailed'));
          break;
        case 403:
          useTokenStore().$reset();
          location.replace('/');
          break;
        case 409:
          displayValidationErrors(apiResponse, t('alert.errorOccurred'));
          break;
        case 426:
          openToast(apiResponse.ErrorMessage ?? t('alert.updateApp'), ToastType.WARNING, '', 0);

          if (apiResponse?.ValidationErrors?.length > 0) {
            openOkAlert(
              apiResponse.ErrorMessage ?? t('alert.updateApp'),
              formatValidationErrors(apiResponse.ValidationErrors)
            );
          }

          break;
        case 500:
          displayValidationErrors(apiResponse, t('alert.serverError'));
          break;
        default:
          displayValidationErrors(apiResponse, t('alert.requestFailed'));
          break;
      }
    },
    onResponse({ response }) {
      const apiResponse = response._data as ResponseDTO<unknown>;

      if (response.status === 404) {
        openToast(t('alert.requestFailed'), ToastType.ERROR);

        return;
      }
      
      if (apiResponse?.HasErrors) {
        displayValidationErrors(apiResponse, t('alert.requestFailed'));
      } 
    }
  });
  
  const appApiFetchV2 = $fetch.create({
    baseURL: config.public.apiBase,
    async onRequest(context) {
      const accessToken = await tokenStore.getAccessToken();
      const config = useRuntimeConfig();
  
      context.options.headers.append(
        'Authorization', `Bearer ${accessToken.token}`
      );
      context.options.headers.append(
        'Localization', locale.value
      );
      context.options.headers.append(
        'Request-Source', config.public.requestSource
      );
    },
    onRequestError() {
      openToast(t('alert.requestFailed'), ToastType.ERROR);
    },
    onResponseError({ response }) {
      // have to call AsErrorResponse in appapi on all badrequests calls 

      const apiResponse = response._data as ResponseDTO<unknown>;

      switch (response?.status) {
        case 400:
          displayValidationErrors(apiResponse, t('alert.requestFailed'));
          break;
        case 403:
          useTokenStore().$reset();
          location.replace('/');
          break;
        case 409:
          displayValidationErrors(apiResponse, t('alert.errorOccurred'));
          break;  
        case 426:
          openToast(apiResponse.ErrorMessage ?? t('alert.updateApp'), ToastType.WARNING, '', 0);

          if (apiResponse?.ValidationErrors?.length > 0) {
            openOkAlert(
              apiResponse.ErrorMessage ?? t('alert.updateApp'),
              formatValidationErrors(apiResponse.ValidationErrors)
            );
          }
          
          break;      
        case 500:
          displayValidationErrors(apiResponse, t('alert.serverError'));
          break;      
        default:
          displayValidationErrors(apiResponse, t('alert.requestFailed'));
          break;
      }
    },
    onResponse({ response }) {
      const apiResponse = response._data as ResponseDTO<unknown>;

      if (response.status === 404) {
        openToast(t('alert.requestFailed'), ToastType.ERROR);

        return;
      }

      if (apiResponse?.HasErrors) {
        displayValidationErrors(apiResponse, t('alert.requestFailed'));
      }
    }
  });
  
  return {
    provide: {
      appApiFetchV2,
      appApiFetchV2Anonymous
    }
  };
});
  